import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';
import { ChipSelectSearch } from '../../components/muiCustomized/ChipSelectSearchable';

function CreateTeamModal({
  onTeamCreated,
  closeModal,
  workspaceId,
}) {
  const { displayNotification } = useContext(NotificationContext);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [members, setMembers] = useState([]);

  const fetchMembers = async () => {
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.members(workspaceId),
      params: {
        pageSize: 0,
      },
    });
    if (success) {
      const filteredMembers = data.members
        .filter(({ haveJoined }) => haveJoined)
        .map((item) => ({
          id: item.id,
          name: `${item.user.firstName} ${item.user.lastName}`,
        }));
      filteredMembers.sort((item1, item2) => {
        if (item1.name > item2.name) return 1;
        if (item1.name < item2.name) return -1;
        return 0;
      });
      setMembers(filteredMembers);
    } else {
      displayNotification({
        message: 'There was an issue fetching members in this workspace. Please try again',
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateTeam = async () => {
    setIsCreating(true);
    const { data, success } = await sendRequest({
      method: 'post',
      url: API.WORKSPACE.createTeam(workspaceId),
      data: {
        name,
        managers: selectedManagers,
        members: selectedMembers,
      },
    });
    if (success) {
      setTimeout(() => {
        onTeamCreated({
          id: data?.team?.id,
          name,
          managers: selectedManagers,
          members: selectedMembers,
        });
        closeModal();
        displayNotification({
          message: `Team “${name}” created.`,
          severity: 'success',
          autoHideDuration: 3000,
        });
      }, 500);
    } else {
      setFormErrors(data);
    }
  };

  const handleSetManagerNames = (e) => {
    setSelectedManagers(e.target.value);
  };

  const handleSetMemberNames = (e) => {
    setSelectedMembers(e.target.value);
  };

  const handleSelectAllManagers = () => {
    setSelectedManagers(members.filter((member) => !selectedMembers.includes(member)));
  };

  const handleSelectAllMembers = () => {
    setSelectedMembers(members.filter((member) => !selectedManagers.includes(member)));
  };

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c create-team-modal">
      <Box style={{
        width: 448,
        padding: 24,
        background: '#fff',
        borderRadius: 8,
        maxHeight: 768,
        overflowY: 'auto',
        position: 'relative',
      }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={closeModal}
          className={'absolute top-4 right-4 bg-transparent border-0 p-0 text-ink-500 '
            + 'opacity-70 hover:opacity-100 transition-opacity'}
        >
          <X size={16} />
        </button>
        <h5 className="text-lg font-semibold text-ink-900 mb-4">Create Team</h5>
        <div className="space-y-4">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="create-team-name" className="block text-sm font-medium text-ink-900 mb-1.5">
              Team Name
            </label>
            <input
              id="create-team-name"
              type="text"
              placeholder="Required"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={'w-full px-3 py-2 border border-rule rounded-[8px] text-sm bg-white '
                + 'text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 '
                + 'focus:ring-brand-500/20 focus:border-brand-500 transition'}
            />
            {formErrors?.name && (
              <p className="text-xs text-red-600 mt-1 mb-0">{formErrors.name}</p>
            )}
          </div>
          <ChipSelectSearch
            redesign
            id="managers"
            label="Add Leaders"
            placeholder="Required"
            selectAllBtn
            selectedMembers={selectedManagers}
            handleChange={handleSetManagerNames}
            handleSelectAll={handleSelectAllManagers}
            members={members.filter((member) => !selectedMembers.includes(member))}
          />
          <ChipSelectSearch
            redesign
            id="members"
            label="Add Team Members"
            placeholder="Required"
            selectAllBtn
            selectedMembers={selectedMembers}
            handleChange={handleSetMemberNames}
            handleSelectAll={handleSelectAllMembers}
            members={members.filter((member) => !selectedManagers.includes(member))}
          />
        </div>
        <div className="flex items-center justify-start gap-2 mt-4">
          <button
            type="button"
            onClick={closeModal}
            className={'inline-flex items-center justify-center px-4 py-2 border border-rule '
              + 'rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition'}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateTeam}
            disabled={isCreating || !(name && selectedManagers.length && selectedMembers.length)}
            className={'inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white '
              + 'rounded-[8px] text-sm font-medium hover:bg-[#458a44] disabled:bg-[#53A551]/40 '
              + 'disabled:cursor-not-allowed transition'}
          >
            {isCreating ? 'Creating...' : 'Create Team'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

CreateTeamModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  onTeamCreated: PropTypes.func.isRequired,
  workspaceId: PropTypes.number.isRequired,
};

export default CreateTeamModal;
